package ai.jessie.web.invoices

import ai.jessie.shared.invoices.InvoiceDraftRequest
import ai.jessie.shared.invoices.InvoiceOptionsResponse
import ai.jessie.shared.invoices.InvoicePreviewResponse
import ai.jessie.shared.invoices.InvoiceProblemResponse
import ai.jessie.shared.invoices.InvoiceResponse
import ai.jessie.web.onboarding.apiRequest
import ai.jessie.web.onboarding.decodeProblem
import ai.jessie.web.onboarding.validOrganisationId
import androidx.compose.runtime.staticCompositionLocalOf
import kotlinx.serialization.json.Json

// Step 2 and 6 choices for a customer, currency and invoice date; every value is optional.
data class InvoiceOptionsQuery(
    val customerId: String? = null,
    val currency: String? = null,
    val issueDate: String? = null,
)

// UC-013. Preview calculates without writing; create stores one complete Draft (steps 10-11).
interface InvoicePort {
    suspend fun options(
        organisationId: String,
        query: InvoiceOptionsQuery = InvoiceOptionsQuery(),
    ): InvoiceOptionsResponse

    suspend fun preview(
        organisationId: String,
        request: InvoiceDraftRequest,
    ): InvoicePreviewResponse

    suspend fun create(organisationId: String, request: InvoiceDraftRequest): InvoiceResponse

    suspend fun list(organisationId: String): List<InvoiceResponse>

    suspend fun read(organisationId: String, invoiceId: String): InvoiceResponse
}

val LocalInvoicePort =
    staticCompositionLocalOf<InvoicePort> {
        error("Invoice port must be supplied by the application")
    }

class InvoiceRequestFailure(val status: Int, val detail: InvoiceProblemResponse) :
    Exception("Invoice request failed")

class BrowserInvoicePort : InvoicePort {
    private val json = Json { ignoreUnknownKeys = true }

    override suspend fun options(organisationId: String, query: InvoiceOptionsQuery) =
        json.decodeFromString<InvoiceOptionsResponse>(
            request(organisationId, "/options${query.encoded()}")
        )

    override suspend fun preview(organisationId: String, request: InvoiceDraftRequest) =
        json.decodeFromString<InvoicePreviewResponse>(
            request(organisationId, "/preview", json.encodeToString(request))
        )

    override suspend fun create(organisationId: String, request: InvoiceDraftRequest) =
        json.decodeFromString<InvoiceResponse>(
            request(organisationId, "", json.encodeToString(request), HTTP_CREATED)
        )

    override suspend fun list(organisationId: String) =
        json.decodeFromString<List<InvoiceResponse>>(request(organisationId, ""))

    override suspend fun read(organisationId: String, invoiceId: String): InvoiceResponse {
        require(validInvoiceId(invoiceId))
        return json.decodeFromString<InvoiceResponse>(request(organisationId, "/$invoiceId"))
    }

    private suspend fun request(
        organisationId: String,
        path: String,
        body: String? = null,
        success: Int = HTTP_OK,
    ): String {
        require(validOrganisationId(organisationId))
        return apiRequest(json, "/organisations/$organisationId/invoices$path", body, success) {
            status,
            text ->
            json.invoiceFailure(status, text)
        }
    }
}

// Shared by the invoice and artefact ports: the problem body, or none when it is not JSON.
internal fun Json.invoiceFailure(status: Int, text: String): InvoiceRequestFailure {
    val detail = decodeProblem(text, InvoiceProblemResponse(""))
    return InvoiceRequestFailure(status, detail)
}

// Only a well-formed customer ID reaches the server; currency and date are sent as entered so that
// the server reports UNSUPPORTED or INVALID itself.
private fun InvoiceOptionsQuery.encoded(): String {
    customerId?.let { require(validInvoiceId(it)) }
    val query =
        listOfNotNull(
            customerId?.let { "customerId=$it" },
            currency?.let { "currency=${encodeURIComponent(it)}" },
            issueDate?.let { "issueDate=${encodeURIComponent(it)}" },
        )
    return if (query.isEmpty()) "" else "?${query.joinToString("&")}"
}

private external fun encodeURIComponent(value: String): String

// Invoice public IDs share the organisation ID format.
fun validInvoiceId(value: String) = validOrganisationId(value)

private const val HTTP_OK = 200
private const val HTTP_CREATED = 201
